/**
 * SignerKiosk — the customer-facing signer surface (Bill 2026-06-24).
 *
 * Full-screen, token-authorized signing display. In-branch today (the FSR's
 * ASUS MB14AHD second screen); the same surface is the Phase-2 remote/RCP
 * signing link. NO portal chrome, NO B2C auth — the capability token in the URL
 * (`?t=`) is the authority.
 *
 * Flow: poll GET /v3/signing/kiosk/state?token= for the step the FSR has pushed
 * → render the document prominently + a compact signature action bar → POST the
 * signature to /v3/signing/kiosk/capture (token-authorized) → wait for the next
 * document. The capture routes server-side into InternalSigningService (audit
 * row + PCB audit page) — no SignNow.
 *
 * Adopt-once / apply-everywhere (Bill 2026-07-08): like DocuSign/Adobe, the
 * signer ADOPTS one signature the first time they need to sign (DRAW a canvas
 * OR TYPE their legal name in a chosen cursive style), held in kiosk session
 * state. Every subsequent signature spot re-applies that adopted signature with
 * a single "Apply signature" tap — no re-drawing/re-typing. See
 * AdoptSignatureModal + signWithAdopted().
 *
 * Server contract UNCHANGED: each /capture still receives ONE signature for the
 * pushed step, in the STEP's captureMode. When a step's captureMode differs from
 * the adopted mode we still send the step's mode but reuse the adopted bytes —
 * drawn steps reuse the adopted PNG/SVG, typed steps reuse the adopted name,
 * click steps send a click attestation (no signature). The adopted signature
 * carries BOTH a raster and a name so any mode can be served.
 *
 * Phase A (Bill 2026-07-07): steps carry stepKind 'capture' | 'consent'.
 * Consent steps render KioskConsentStep (scroll-gated E-Sign consent); capture
 * steps render the current document's PDF (KioskDocumentViewer) with the
 * signature action bar beneath it.
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { SignatureCaptureRouter } from '@retail-ops/signing-capture';
import type {
  PresentationStateResponse,
  SignerPlanItem,
  PresentationStep,
  SignatureCaptureResult,
  SignatureCaptureMode,
  KioskCaptureRequest,
  KioskCaptureResponse,
} from '@retail-ops/shared-types';
import { ENV } from '@/constants/env';
import { kioskFetch, setKioskSession, clearKioskSession } from './kioskSession';
import { PORTAL_VERSION } from '@/constants/version';
import { KioskConsentStep } from './KioskConsentStep';
import { KioskReviewStep } from './KioskReviewStep';
import { KioskDocumentViewer } from './KioskDocumentViewer';
import { AdoptSignatureModal, type AdoptedSignature } from './AdoptSignatureModal';

const POLL_MS = 1500;
/** Adopted signature stamped with the signer it belongs to (re-adopt per signer). */
type AdoptedForSigner = AdoptedSignature & { signerIndex: number };

// Real PCB logo (white) — inline per brand rule (no external asset).
// viewBox cropped to the PCB mark; the source path carried a stray standalone
// "P" glyph at x≈120 (Bill 2026-07-13 Item 24) which is removed below.
function PcbLogo({ width = 96 }: { width?: number }): JSX.Element {
  return (
    <svg width={width} height={Math.round(width * 63 / 110)} viewBox="0 0 110 63" aria-label="Phelps County Bank" role="img">
      <path
        fill="#fff"
        d="M93.253 0H79.17v12.304h5.877c2.834 0 5.562.24 8.176.716 2.62.475 4.924 1.402 6.913 2.774 1.99 1.372 3.585 3.32 4.794 5.824.846 1.77 1.394 3.935 1.644 6.475V13.126C106.58 5.876 100.614 0 93.254 0zm-2.936 39.23c1.143-.194 2.15-.575 3.02-1.15.874-.563 1.565-1.36 2.1-2.398.525-1.033.787-2.387.787-4.065 0-1.67-.262-3.026-.786-4.064-.53-1.032-1.227-1.83-2.102-2.4-.87-.568-1.87-.95-3.02-1.142-1.148-.2-2.34-.3-3.578-.3h-7.503v15.82h7.503c1.24 0 2.436-.1 3.58-.3zm-69.344 0c1.15-.194 2.156-.575 3.025-1.15.87-.563 1.572-1.36 2.096-2.398.524-1.033.792-2.387.792-4.065 0-1.67-.268-3.026-.792-4.064-.524-1.032-1.227-1.83-2.096-2.4-.87-.568-1.876-.95-3.025-1.142-1.143-.2-2.334-.3-3.573-.3H9.897v15.82H17.4c1.24 0 2.43-.1 3.573-.3zm79.163 7.935c-1.99 1.372-4.3 2.3-6.913 2.78-2.62.475-5.342.71-8.176.71h-6.884l-10.242-.106L67.987 0H13.303C6.11 0 .263 5.62 0 12.645h16.525c2.78 0 5.46.234 8.033.72 2.572.476 4.835 1.414 6.794 2.793 1.96 1.384 3.525 3.337 4.71 5.864 1.186 2.522 1.78 5.83 1.78 9.918 0 4.088-.6 7.395-1.78 9.917-1.185 2.522-2.75 4.475-4.71 5.853-1.953 1.384-4.216 2.317-6.794 2.798-2.58.48-5.252.715-8.033.715H9.76v11.29c1.13.305 2.316.487 3.543.487h79.95c7.36 0 13.327-5.882 13.327-13.126V34.86c-.25 2.54-.798 4.704-1.644 6.475-1.215 2.51-2.81 4.458-4.8 5.83zm-34.62-23.8l-7.445.14c-3.023.09-5.06 1.05-5.06 1.05-4.186 1.696-3.86 7.098-3.86 7.098-.047 5.864 5.116 6.96 5.116 6.96 2.323 1.098 11.2.775 11.2.775v10.944c-8.972.457-12.688-.37-12.688-.37-8.093-1.19-11.112-5.536-11.112-5.536-4.276-4.58-3.763-13.97-3.763-13.97.09-8.967 4.096-12.64 4.096-12.64 3.435-4.022 9.015-5.083 9.015-5.083 3.388-.822 14.506-.78 14.506-.78v11.413h-.005z"
      />
    </svg>
  );
}

// ── styles ───────────────────────────────────────────────────────────────
const FONT = "'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif";
const rootS: CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 9999, background: '#eef3f9',
  display: 'flex', flexDirection: 'column', fontFamily: FONT, color: '#0f172a',
};
// Slim header (Bill 2026-07-13 Item 24: conserve vertical space). Desktop
// capture steps drop this header entirely — the left rail owns the brand.
const headerS: CSSProperties = {
  background: '#1a406a', color: '#fff', padding: '7px 18px',
  display: 'flex', alignItems: 'center', gap: 12, flex: '0 0 auto',
};
// Body is a flex column so a capture step can give the document all the room
// and dock the signature action bar at the bottom.
const bodyS: CSSProperties = {
  flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden',
};
// Centering is done by the child's auto margins (.kiosk-center > * in layout.css), NOT by
// align-items/justify-content: a flex-centered child taller than a phone screen overflows
// ABOVE the scroll origin, and its top cannot be scrolled to (Bill's 2026-10-13 smoke test:
// the remote plan list's first lines were cut off under the header).
const centerWrapS: CSSProperties = {
  flex: 1, display: 'flex', padding: 24, overflow: 'auto',
};
const centerCardS: CSSProperties = {
  background: '#fff', borderRadius: 12, padding: '40px 36px', maxWidth: 560, width: '100%',
  textAlign: 'center', boxShadow: '0 10px 36px rgba(0,0,0,0.12)',
};
const bigS: CSSProperties = { fontSize: 24, fontWeight: 700, color: '#1a406a', margin: '0 0 10px' };
const subS: CSSProperties = { fontSize: 16, color: '#475569', margin: 0, lineHeight: 1.5 };
const checkS: CSSProperties = {
  width: 76, height: 76, borderRadius: '50%', background: '#386732', color: '#fff',
  fontSize: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px',
};
const versionFooterS: CSSProperties = {
  flex: '0 0 auto', padding: '6px 26px 10px', fontSize: 11, color: '#64748b', textAlign: 'right',
};

// ── capture-step layout: document prominent + compact action bar ──────────
const captureRootS: CSSProperties = {
  flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
};
const docAreaS: CSSProperties = {
  flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '16px 22px 12px',
};
const actionBarS: CSSProperties = {
  flex: '0 0 auto', background: '#fff', borderTop: '1px solid #d7e0ec',
  boxShadow: '0 -6px 20px rgba(0,0,0,0.08)', padding: '14px 22px',
  display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
};
const barTextS: CSSProperties = { fontSize: 15, color: '#1f2937', fontWeight: 700, flex: '1 1 240px', minWidth: 180 };
const barSubS: CSSProperties = { display: 'block', fontSize: 13, color: '#64748b', fontWeight: 400, marginTop: 2 };
const previewChipS: CSSProperties = {
  height: 56, minWidth: 120, maxWidth: 220, border: '1px solid #e2e8f0', borderRadius: 8,
  background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '4px 10px', flex: '0 0 auto',
};
const previewImgS: CSSProperties = { maxHeight: 48, maxWidth: 200, objectFit: 'contain', display: 'block' };
const primaryBarBtnS = (enabled: boolean, color: string): CSSProperties => ({
  padding: '13px 26px', borderRadius: 8, fontSize: 16, fontWeight: 700, color: '#fff',
  background: enabled ? color : '#9ca3af', border: `1px solid ${enabled ? color : '#9ca3af'}`,
  cursor: enabled ? 'pointer' : 'not-allowed', flex: '0 0 auto',
});
const changeBtnS: CSSProperties = {
  padding: '10px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#2968ad',
  background: 'transparent', border: '1px solid transparent', cursor: 'pointer', flex: '0 0 auto',
};
const barErrorS: CSSProperties = {
  flexBasis: '100%', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca',
  borderRadius: 8, padding: '10px 14px', fontSize: 14,
};
const overlayS: CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, overflow: 'auto',
};

// ── desktop left-rail layout (Bill 2026-07-13 Item 24) ────────────────────
// ≥1024px: brand + document label + signer + signature controls consolidate
// into a left nav pane; the document viewer takes the full remaining canvas.
const desktopRowS: CSSProperties = { flex: 1, display: 'flex', minHeight: 0 };
const railS: CSSProperties = {
  flex: '0 0 300px', display: 'flex', flexDirection: 'column', background: '#fff',
  borderRight: '1px solid #d7e0ec', minHeight: 0, overflowY: 'auto',
};
const railBrandS: CSSProperties = {
  background: '#1a406a', color: '#fff', padding: '10px 16px',
  display: 'flex', alignItems: 'center', gap: 10, flex: '0 0 auto',
};
const railBodyS: CSSProperties = {
  padding: '16px 16px 20px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1,
};
const railSignerChipS: CSSProperties = {
  background: '#d9edf7', borderLeft: '4px solid #2968ad', borderRadius: 6,
  padding: '10px 12px', fontSize: 13, color: '#1a406a', lineHeight: 1.4,
};
const railDocTitleS: CSSProperties = { fontSize: 16, fontWeight: 700, color: '#1a406a', margin: 0, lineHeight: 1.35 };
const railBtnS = (enabled: boolean, color: string): CSSProperties => ({
  ...primaryBarBtnS(enabled, color), width: '100%', textAlign: 'center' as const,
});
const railPreviewS: CSSProperties = {
  border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 10px', minHeight: 56,
};
const railHintS: CSSProperties = { fontSize: 13, color: '#64748b', lineHeight: 1.45, margin: 0 };
const desktopDocAreaS: CSSProperties = {
  flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '12px 16px',
};
// "Now signing" chip on the mobile action bar (Item 25).
const barSignerS: CSSProperties = {
  flexBasis: '100%', fontSize: 13, color: '#1a406a', fontWeight: 700, margin: '-2px 0 -6px',
};

// ── Remote e-mail challenge (BACKLOG-1738, Bill 2026-10-05) ────────────────
// The e-mailed link opens HERE. Step 1: the signer types the e-mail address the
// link was sent to (5 tries, then "contact your banker"). Step 2: a one-time code
// is e-mailed to that address; it works for 90 seconds and allows 3 tries, and a
// new code can be requested when it expires. Nothing is read over the phone.
// Limits come from the server (Admin Panel settings), never from this page.
interface RemoteChallenge {
  stage: 'email' | 'sending' | 'code' | 'locked';
  emailAttemptsLeft: number;
  codeExpiresAt: string | null;
  codeAttemptsLeft: number;
  canResend: boolean;
  resendsLeft: number;
  message: string | null;
}

const challengeInputS: CSSProperties = {
  fontSize: 18, width: '100%', boxSizing: 'border-box', padding: '10px 12px',
  margin: '16px 0', border: '1px solid #cbd5e1', borderRadius: 8,
};
const codeInputS: CSSProperties = {
  fontSize: 28, letterSpacing: 8, textAlign: 'center', width: 220,
  padding: '10px 12px', margin: '16px auto', display: 'block',
  border: '1px solid #cbd5e1', borderRadius: 8,
};
const linkBtnS: CSSProperties = {
  background: 'none', border: 'none', color: '#2968ad', fontSize: 15, fontWeight: 600,
  textDecoration: 'underline', cursor: 'pointer', marginTop: 14, padding: 4,
};

function secondsUntil(iso: string | null, now: number): number {
  if (!iso) return 0;
  const t = Date.parse(iso.endsWith('Z') ? iso : `${iso}Z`);
  return Number.isNaN(t) ? 0 : Math.max(0, Math.ceil((t - now) / 1000));
}

function RemoteChallengeGate({
  token, challenge, onChanged,
}: { token: string; challenge: RemoteChallenge | null; onChanged: () => void }): JSX.Element {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const stage = challenge?.stage ?? 'email';
  const left = secondsUntil(challenge?.codeExpiresAt ?? null, now);
  const codeSpent = stage === 'code' && (left === 0 || (challenge?.codeAttemptsLeft ?? 0) === 0);
  const canResend = !!challenge && (challenge.canResend || codeSpent) && challenge.resendsLeft > 0;

  const post = async (path: string, body: Record<string, string>): Promise<boolean> => {
    setBusy(true);
    setErr(null);
    try {
      const r = await kioskFetch(`${ENV.apiBaseUrl}/v3/signing/kiosk/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...body }),
      });
      const j = (await r.json()) as { success: boolean; error?: string; session?: string | null };
      if (!j.success) setErr(j.error ?? 'That did not work — please try again.');
      // A good code returns this device's session (§3E); every later call carries it.
      if (j.success && j.session) setKioskSession(j.session);
      return j.success;
    } catch {
      setErr('Connection problem — please try again in a moment.');
      return false;
    } finally {
      setBusy(false);
      onChanged();
    }
  };

  const submitEmail = async (): Promise<void> => {
    if (busy || !email.includes('@')) return;
    await post('email', { email: email.trim() });
  };
  const submitCode = async (): Promise<void> => {
    if (busy || code.length !== 6) return;
    if (await post('otp', { otp: code })) setCode('');
  };
  const resend = async (): Promise<void> => {
    if (busy) return;
    setCode('');
    await post('otp/resend', {});
  };

  if (stage === 'locked') {
    return (
      <div style={centerWrapS} className="kiosk-center">
        <div style={centerCardS}>
          <h1 style={bigS}>This link is locked</h1>
          <p style={subS}>{challenge?.message ?? 'Please contact your banker.'}</p>
        </div>
      </div>
    );
  }

  if (stage === 'email') {
    const attemptsLeft = challenge?.emailAttemptsLeft;
    return (
      <div style={centerWrapS} className="kiosk-center">
        <div style={centerCardS}>
          <h1 style={bigS}>Confirm your e-mail address</h1>
          <p style={subS}>
            Enter the e-mail address this link was sent to. We'll e-mail you a one-time code to open your documents.
          </p>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submitEmail(); }}
            aria-label="E-mail address"
            style={challengeInputS}
          />
          {err && <div role="alert" style={{ ...barErrorS, marginBottom: 10 }}>{err}</div>}
          {err && attemptsLeft !== undefined && attemptsLeft > 0 && (
            <p style={{ ...subS, fontSize: 14, marginBottom: 10 }}>
              {attemptsLeft} {attemptsLeft === 1 ? 'try' : 'tries'} left before this link locks.
            </p>
          )}
          <button
            type="button"
            style={primaryBarBtnS(!busy && email.includes('@'), '#2968ad')}
            disabled={busy || !email.includes('@')}
            onClick={() => void submitEmail()}
          >
            {busy ? 'Checking…' : 'Send my code'}
          </button>
        </div>
      </div>
    );
  }

  if (stage === 'sending') {
    return (
      <div style={centerWrapS} className="kiosk-center">
        <div style={centerCardS}>
          <h1 style={bigS}>Sending your code…</h1>
          <p style={subS}>We're e-mailing a one-time code to the address you entered. This takes a few seconds.</p>
          {(challenge?.message || err) && (
            <div role="alert" style={{ ...barErrorS, margin: '14px 0 10px' }}>{err ?? challenge?.message}</div>
          )}
          {canResend && (
            <button type="button" style={linkBtnS} disabled={busy} onClick={() => void resend()}>
              Send a new code
            </button>
          )}
        </div>
      </div>
    );
  }

  // stage === 'code'
  const mm = Math.floor(left / 60);
  const ss = String(left % 60).padStart(2, '0');
  return (
    <div style={centerWrapS} className="kiosk-center">
      <div style={centerCardS}>
        <h1 style={bigS}>Enter your code</h1>
        <p style={subS}>
          We e-mailed a 6-digit code to the address you entered. Check your inbox, and your junk folder if it isn't there.
        </p>
        <input
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
          value={code}
          disabled={codeSpent}
          onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={(e) => { if (e.key === 'Enter') void submitCode(); }}
          aria-label="One-time code"
          style={codeInputS}
        />
        {err && <div role="alert" style={{ ...barErrorS, marginBottom: 10 }}>{err}</div>}
        {!codeSpent && (
          <p style={{ ...subS, fontSize: 14, marginBottom: 12 }} aria-live="polite">
            Code expires in {mm}:{ss} · {challenge?.codeAttemptsLeft ?? 0} {(challenge?.codeAttemptsLeft ?? 0) === 1 ? 'try' : 'tries'} left
          </p>
        )}
        {codeSpent && (
          <p style={{ ...subS, fontSize: 14, marginBottom: 12 }}>
            {left === 0 ? 'This code has expired.' : 'Too many incorrect tries.'} Request a new code below.
          </p>
        )}
        {!codeSpent && (
          <button
            type="button"
            style={primaryBarBtnS(!busy && code.length === 6, '#2968ad')}
            disabled={busy || code.length !== 6}
            onClick={() => void submitCode()}
          >
            {busy ? 'Checking…' : 'Continue'}
          </button>
        )}
        {canResend ? (
          <button type="button" style={linkBtnS} disabled={busy} onClick={() => void resend()}>
            Send a new code
          </button>
        ) : (
          !codeSpent && (
            <p style={{ ...subS, fontSize: 13, marginTop: 14 }}>
              You can request a new code in {mm}:{ss}.
            </p>
          )
        )}
      </div>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────
function initialsOf(name: string): string {
  const seed = name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase())
    .join('')
    .slice(0, 4);
  return seed || 'X';
}

const BANK_PHONE = '800-667-5202';

const progressS: CSSProperties = {
  fontSize: 13, fontWeight: 600, color: '#1a406a', padding: '6px 12px', textAlign: 'center',
};

/** What a remote signer will read and sign, shown before they start (Bill 2026-10-07). */
function RemotePlanList({ plan }: { plan: SignerPlanItem[] }): JSX.Element {
  const toSign = plan.filter((p) => p.kind === 'sign').length;
  const toRead = plan.length - toSign;
  const parts = [
    toSign > 0 ? `sign ${toSign} document${toSign === 1 ? '' : 's'}` : null,
    toRead > 0 ? `read ${toRead} disclosure${toRead === 1 ? '' : 's'}` : null,
  ].filter(Boolean);
  return (
    <div style={{ background: '#fff', border: '1px solid #d6e0ec', borderRadius: 10, padding: '14px 18px', marginBottom: 14 }}>
      <div style={{ fontWeight: 700, color: '#1a406a', marginBottom: 6 }}>What's ahead</div>
      <div style={{ fontSize: 14, marginBottom: 8 }}>
        After this consent you'll {parts.join(' and ')}. You can download a copy of everything when you finish.
      </div>
      <ol style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.6 }}>
        {plan.map((p) => (
          <li key={p.documentId}>
            {p.title} <span style={{ color: '#5b6b7d' }}>({p.kind === 'sign' ? 'sign' : 'read'})</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Remote finish (BACKLOG-1760): no banker wording; the signer downloads their copy right away. */
function RemoteDone({ token, state, plan, onReverify }: { token: string; state: PresentationStateResponse | null; plan: SignerPlanItem[]; onReverify: () => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const until = state?.copyAvailableUntil ? new Date(state.copyAvailableUntil) : null;
  const open = !!until && until.getTime() > Date.now();
  const signed = plan.filter((p) => p.kind === 'sign');

  const download = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const r = await kioskFetch(`${ENV.apiBaseUrl}/v3/signing/kiosk/copy?token=${encodeURIComponent(token)}`);
      if (r.ok && (r.headers.get('content-type') ?? '').includes('application/pdf')) {
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Phelps-County-Bank-documents-${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        let msg = `We couldn't get your documents just now. Please try again, or call us at ${BANK_PHONE}.`;
        try {
          const j = (await r.json()) as { error?: string };
          if (j.error) msg = j.error;
        } catch { /* not JSON */ }
        setErr(msg);
      }
    } catch {
      setErr(`We couldn't get your documents just now. Please try again, or call us at ${BANK_PHONE}.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={centerWrapS} className="kiosk-center">
      <div style={centerCardS}>
        <div style={checkS}>✓</div>
        <h1 style={bigS}>You're all done</h1>
        <p style={subS}>
          Thank you{state?.currentSignerName ? `, ${state.currentSignerName}` : ''}.{' '}
          {signed.length > 0
            ? `You signed ${signed.length} document${signed.length === 1 ? '' : 's'}. We'll take it from here.`
            : "We'll take it from here."}
        </p>
        {open ? (
          <>
            <button type="button" onClick={() => void download()} disabled={busy}
              style={{ marginTop: 12, padding: '12px 22px', fontSize: 16, fontWeight: 600, color: '#fff', background: '#2968ad', border: 0, borderRadius: 8, cursor: busy ? 'wait' : 'pointer' }}>
              {busy ? 'Preparing your documents…' : 'Download your documents (PDF)'}
            </button>
            <p style={{ ...subS, fontSize: 13, marginTop: 10 }}>
              Includes what you signed and the disclosures for your accounts. Available from this page until{' '}
              {until!.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.
            </p>
            {err && <div role="alert" style={{ color: '#b10505', fontSize: 14, marginTop: 8 }}>{err}</div>}
          </>
        ) : (
          <>
            {/* Bill 2026-10-07: coming back and confirming it's you again restarts the download window. */}
            <button type="button" onClick={() => { clearKioskSession(); onReverify(); }}
              style={{ marginTop: 12, padding: '12px 22px', fontSize: 16, fontWeight: 600, color: '#fff', background: '#2968ad', border: 0, borderRadius: 8, cursor: 'pointer' }}>
              Verify it's you to download your documents
            </button>
            <p style={{ ...subS, fontSize: 13, marginTop: 10 }}>
              We'll e-mail you a new code. Or call us at {BANK_PHONE} for a copy.
            </p>
          </>
        )}
        <p style={{ ...subS, fontSize: 13, marginTop: 14 }}>You can close this page.</p>
      </div>
    </div>
  );
}

const SAVE_FAILED = "We couldn't save your signature just now. It's kept — tap Apply signature to try again.";

/** One save attempt. A reply that is not JSON (a network filter's block page, a gateway timeout) or no
 *  reply at all counts as a failed attempt - it is never mistaken for the server's answer. */
async function postCaptureOnce(body: KioskCaptureRequest): Promise<KioskCaptureResponse | null> {
  try {
    const r = await kioskFetch(`${ENV.apiBaseUrl}/v3/signing/kiosk/capture`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const text = await r.text();
    try {
      return JSON.parse(text) as KioskCaptureResponse;
    } catch {
      return null;
    }
  } catch {
    return null;
  }
}

/** Saves a signature, retrying once on its own after a failed attempt (Bill 2026-10-07: a save failed in
 *  transit and the signer had to start over). Safe to repeat: the save names its document, so the server
 *  never applies it elsewhere and reports a save that already landed as saved. */
async function saveCapture(body: KioskCaptureRequest): Promise<KioskCaptureResponse | null> {
  let failed = 0;
  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt > 0) await new Promise((res) => window.setTimeout(res, 1500));
    const j = await postCaptureOnce(failed > 0 ? { ...body, failedAttempts: failed } : body);
    if (j) return j;
    failed++;
  }
  return null;
}

export function SignerKiosk(): JSX.Element {
  const token =
    new URLSearchParams(window.location.search).get('t') ??
    new URLSearchParams(window.location.search).get('token') ??
    '';

  const [state, setState] = useState<PresentationStateResponse | null>(null);

  /**
   * May THIS signer type their signature instead of drawing it?
   *
   * Risk Mgmt via Bill 2026-07-16: "type to sign will be gated on in-branch per Risk Mgmt.
   * It will only be an option for a remote signer or online applicant."
   *
   * In-branch the customer is at the desk with an FSR and a touchscreen in front of them —
   * there is no reason to accept a font when you can have their hand. Remote and online
   * applicants have no touchscreen guarantee, so typed stays available to them.
   *
   * ALLOW-LIST, not a deny-list, and it fails CLOSED. The surfaces are 'in-branch',
   * 'rcp-remote', 'wet-attested', 'online' and 'online-cart'; only the remote/online ones
   * are named here. An unknown or not-yet-loaded surface gets NO typed option — drawing
   * always works, whereas wrongly offering typed in a branch is the exact thing Risk asked
   * us to stop. A deny-list would do the opposite: a surface added later would silently
   * default to permitting it.
   *
   * `allowTyped=false` removes the tab entirely (see AdoptSignatureModal) rather than
   * leaving it reachable — a hidden-but-reachable typed tab is the same as no rule.
   */
  const allowTypedSignature =
    state?.surface === 'rcp-remote'
    || state?.surface === 'online'
    || state?.surface === 'online-cart';
  const isRemote = state?.surface === 'rcp-remote';
  const plan: SignerPlanItem[] = isRemote ? state?.plan ?? [] : [];
  /** "Document n of N" for a remote signer (Bill 2026-10-07: say up front what is ahead). */
  const progressLine = (documentId: string): JSX.Element | null => {
    if (!isRemote || plan.length === 0) return null;
    const i = plan.findIndex((p) => p.documentId === documentId);
    if (i < 0) return null;
    return <div style={progressS}>Document {i + 1} of {plan.length}</div>;
  };
  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState<'signature' | 'consent' | 'review' | null>(null);

  // Adopt-once state — one signature per signer, reused on every later step.
  const [adopted, setAdopted] = useState<AdoptedForSigner | null>(null);
  const [adopting, setAdopting] = useState(false); // adoption modal open
  const [clicking, setClicking] = useState(false); // click-attestation modal open

  // Desktop (≥1024px) switches capture steps to the left-rail layout (Item 24).
  const [isDesktop, setIsDesktop] = useState<boolean>(
    () => window.matchMedia('(min-width: 1024px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e: MediaQueryListEvent): void => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const submittingRef = useRef(false);
  // Intent = the moment THIS step's signing surface first rendered. Must precede
  // the capture by >= 1s (server rule); signWithAdopted also enforces the gap.
  const intentRef = useRef<string>(new Date().toISOString());

  const fetchState = useCallback(async () => {
    if (!token) { setLoaded(true); return; }
    try {
      const r = await kioskFetch(
        `${ENV.apiBaseUrl}/v3/signing/kiosk/state?token=${encodeURIComponent(token)}`
      );
      const j = (await r.json()) as PresentationStateResponse;
      setState(j);
    } catch {
      /* transient — keep last state, next poll retries */
    } finally {
      setLoaded(true);
    }
  }, [token]);

  useEffect(() => {
    void fetchState();
    const id = window.setInterval(() => {
      if (!submittingRef.current) void fetchState();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [fetchState]);

  // ── derive the active step ───────────────────────────────────────────────
  const st = state?.status;
  const activeStep = st === 'active' ? state?.currentStep ?? null : null;
  // Three step kinds: 'consent' (E-Sign), 'review' (a view-only disclosure —
  // presented and acknowledged, never signed; Bill 2026-09-30), 'capture'.
  const captureStep =
    activeStep && activeStep.stepKind !== 'consent' && activeStep.stepKind !== 'review' ? activeStep : null;
  const consentStep = activeStep && activeStep.stepKind === 'consent' ? activeStep : null;
  const reviewStep = activeStep && activeStep.stepKind === 'review' ? activeStep : null;
  const stepKey = captureStep
    ? `${captureStep.signerIndex}:${captureStep.documentId}:${captureStep.captureMode}`
    : null;

  // On a NEW capture step: reset intent, close any leftover modals + errors.
  useEffect(() => {
    if (stepKey) {
      intentRef.current = new Date().toISOString();
      setAdopting(false);
      setClicking(false);
      setCaptureError(null);
    }
  }, [stepKey]);

  const hasAdoptedForStep = !!(adopted && captureStep && adopted.signerIndex === captureStep.signerIndex);

  // ── capture submit (adopt / apply path — typed & drawn) ──────────────────
  const signWithAdopted = useCallback(
    async (step: PresentationStep, adopt: AdoptedSignature) => {
      submittingRef.current = true;
      setSubmitting(true);
      setCaptureError(null);
      try {
        // Guarantee the >= 1s intent → signature gap the server enforces.
        const intentTs = intentRef.current;
        const gap = Date.now() - new Date(intentTs).getTime();
        if (gap < 1100) await new Promise((res) => window.setTimeout(res, 1100 - gap));

        const mode = step.captureMode as SignatureCaptureMode;
        const body: KioskCaptureRequest = {
          token,
          captureMode: mode, // ALWAYS the pushed step's mode (server: CAPTURE_MODE_MISMATCH)
          intentTimestamp: intentTs,
          signerViewportSize: `${window.innerWidth}x${window.innerHeight}`,
          signerUserAgent: navigator.userAgent,
        };
        if (mode === 'drawn') {
          // Reuse the adopted raster + vector (typed adoptions carry both too).
          body.canvasPngBase64 = adopt.pngBase64;
          body.canvasSvg = adopt.svg;
        } else if (mode === 'typed') {
          // Reuse the adopted legal name (drawn adoptions default it to the name).
          body.typedName = adopt.typedName || step.signerName || '';
          body.canvasSvg = adopt.svg;
        } else {
          // 'click' — attestation only, no signature bytes.
          body.clickAttestation = initialsOf(adopt.typedName || step.signerName || '');
        }

        body.expectedDocumentId = step.documentId;
        const j = await saveCapture(body);
        if (j?.success) {
          setAdopting(false);
          setClicking(false);
          setJustSaved('signature');
          window.setTimeout(() => setJustSaved(null), 2600);
          await fetchState();
        } else if (j?.errorCode === 'STEP_MOVED') {
          await fetchState();
        } else {
          // Close the overlay so the message is seen (it used to sit behind it), but KEEP the adopted
          // signature: "Apply signature" retries with one tap - nothing is redrawn.
          setAdopting(false);
          setCaptureError(j?.error ?? SAVE_FAILED);
        }
      } catch {
        setAdopting(false);
        setCaptureError(SAVE_FAILED);
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    [token, fetchState]
  );

  // Adoption complete → remember it for this signer AND sign the current step.
  const handleAdopt = useCallback(
    (a: AdoptedSignature) => {
      if (!captureStep) return;
      const stamped: AdoptedForSigner = { ...a, signerIndex: captureStep.signerIndex };
      setAdopted(stamped);
      void signWithAdopted(captureStep, stamped);
    },
    [captureStep, signWithAdopted]
  );

  // Apply the already-adopted signature to the current step (one tap).
  const handleApply = useCallback(() => {
    if (!captureStep || !adopted) return;
    void signWithAdopted(captureStep, adopted);
  }, [captureStep, adopted, signWithAdopted]);

  // ── click-attestation path (reuses the ClickToSignCapture primitive) ─────
  const handleClickCapture = useCallback(
    async (result: SignatureCaptureResult) => {
      const step = captureStep;
      if (!step) return;
      submittingRef.current = true;
      setSubmitting(true);
      setCaptureError(null);
      try {
        const body: KioskCaptureRequest = {
          token,
          captureMode: step.captureMode as SignatureCaptureMode,
          typedName: result.typedName,
          canvasSvg: result.svgPath ?? result.renderedSvg,
          canvasPngBase64: result.pngBase64,
          clickAttestation: result.initials,
          intentTimestamp: result.intentTimestamp,
          signerViewportSize: result.signerViewportSize,
          signerUserAgent: result.signerUserAgent,
          scrollProgressPct: result.scrollProgressPct,
          expectedDocumentId: step.documentId,
        };
        const j = await saveCapture(body);
        if (j?.success) {
          setClicking(false);
          setJustSaved('signature');
          window.setTimeout(() => setJustSaved(null), 2600);
          await fetchState();
        } else if (j?.errorCode === 'STEP_MOVED') {
          setClicking(false);
          await fetchState();
        } else {
          setClicking(false); // the message showed behind the overlay before
          setCaptureError(j?.error ?? SAVE_FAILED);
        }
      } catch {
        setClicking(false);
        setCaptureError(SAVE_FAILED);
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    [captureStep, token, fetchState]
  );

  const handleConsentSaved = useCallback(() => {
    setJustSaved('consent');
    window.setTimeout(() => setJustSaved(null), 2600);
    void fetchState();
  }, [fetchState]);

  const handleReviewSaved = useCallback(() => {
    setJustSaved('review');
    window.setTimeout(() => setJustSaved(null), 2600);
    void fetchState();
  }, [fetchState]);

  const handleConsentSubmitting = useCallback((s: boolean) => {
    submittingRef.current = s;
  }, []);

  // ── desktop rail actions (vertical variant of the action bar) ────────────
  function renderRailActions(step: PresentationStep): JSX.Element {
    const docLabel = step.docTitle ?? 'this document';

    if (step.captureMode === 'click') {
      return (
        <>
          {captureError && <div role="alert" style={barErrorS}>{captureError}</div>}
          <p style={railHintS}>Review the document on the right, then confirm to sign {docLabel}.</p>
          <button
            type="button"
            style={railBtnS(!submitting, '#2968ad')}
            disabled={submitting}
            onClick={() => setClicking(true)}
          >
            Confirm this document
          </button>
        </>
      );
    }

    if (hasAdoptedForStep && adopted) {
      return (
        <>
          {captureError && <div role="alert" style={barErrorS}>{captureError}</div>}
          <div style={railPreviewS} aria-label="Your adopted signature">
            <img
              src={`data:image/png;base64,${adopted.pngBase64}`}
              alt="Your signature"
              style={previewImgS}
            />
          </div>
          <button
            type="button"
            style={railBtnS(!submitting, '#386732')}
            disabled={submitting}
            onClick={handleApply}
          >
            {submitting ? 'Applying…' : 'Apply signature'}
          </button>
          <button
            type="button"
            style={{ ...changeBtnS, width: '100%' }}
            disabled={submitting}
            onClick={() => setAdopting(true)}
          >
            Change signature
          </button>
        </>
      );
    }

    return (
      <>
        {captureError && <div role="alert" style={barErrorS}>{captureError}</div>}
        <p style={railHintS}>
          Set up your signature once — then a single tap signs every document, including {docLabel}.
        </p>
        <button
          type="button"
          style={railBtnS(!submitting, '#2968ad')}
          disabled={submitting}
          onClick={() => setAdopting(true)}
        >
          Add your signature
        </button>
      </>
    );
  }

  // ── action bar (compact, distinct from the document) ─────────────────────
  function renderActionBar(step: PresentationStep): JSX.Element {
    const docLabel = step.docTitle ?? 'this document';

    if (step.captureMode === 'click') {
      return (
        <div style={actionBarS}>
          {step.signerName && <div style={barSignerS}>Now signing: {step.signerName}</div>}
          {captureError && <div role="alert" style={barErrorS}>{captureError}</div>}
          <div style={barTextS}>
            Confirm this document
            <span style={barSubS}>Review the document above, then confirm to sign {docLabel}.</span>
          </div>
          <button
            type="button"
            style={primaryBarBtnS(!submitting, '#2968ad')}
            disabled={submitting}
            onClick={() => setClicking(true)}
          >
            Confirm this document
          </button>
        </div>
      );
    }

    if (hasAdoptedForStep && adopted) {
      return (
        <div style={actionBarS}>
          {step.signerName && <div style={barSignerS}>Now signing: {step.signerName}</div>}
          {captureError && <div role="alert" style={barErrorS}>{captureError}</div>}
          <div style={previewChipS} aria-label="Your adopted signature">
            <img
              src={`data:image/png;base64,${adopted.pngBase64}`}
              alt="Your signature"
              style={previewImgS}
            />
          </div>
          <div style={barTextS}>
            Your signature is ready
            <span style={barSubS}>Tap Apply signature to sign {docLabel}.</span>
          </div>
          <button
            type="button"
            style={changeBtnS}
            disabled={submitting}
            onClick={() => setAdopting(true)}
          >
            Change signature
          </button>
          <button
            type="button"
            style={primaryBarBtnS(!submitting, '#386732')}
            disabled={submitting}
            onClick={handleApply}
          >
            {submitting ? 'Applying…' : 'Apply signature'}
          </button>
        </div>
      );
    }

    // Not adopted yet for this signer — offer the one-time adoption.
    return (
      <div style={actionBarS}>
        {step.signerName && <div style={barSignerS}>Now signing: {step.signerName}</div>}
        {captureError && <div role="alert" style={barErrorS}>{captureError}</div>}
        <div style={barTextS}>
          Add your signature
          <span style={barSubS}>
            Set up your signature once — then a single tap signs every document, including {docLabel}.
          </span>
        </div>
        <button
          type="button"
          style={primaryBarBtnS(!submitting, '#2968ad')}
          disabled={submitting}
          onClick={() => setAdopting(true)}
        >
          Add your signature
        </button>
      </div>
    );
  }

  // ── render ───────────────────────────────────────────────────────────────
  function renderInner(): JSX.Element {
    if (!token) {
      return (
        <div style={centerWrapS} className="kiosk-center">
          <div style={centerCardS}>
            <h1 style={bigS}>This signing screen isn’t linked yet</h1>
            <p style={subS}>Please ask your banker to connect this screen, or scan the code they provide.</p>
          </div>
        </div>
      );
    }
    if (!loaded) {
      return (
        <div style={centerWrapS} className="kiosk-center">
          <div style={centerCardS}><p style={subS}>Connecting…</p></div>
        </div>
      );
    }
    if (state && state.success === false && (st === 'expired' || st === 'cancelled' || st === 'superseded' || st === 'completed' || !st)) {
      // The server says why the link stopped working ("title|message"), worded for remote or in-branch.
      const [title, message] = (state.error ?? '').includes('|')
        ? (state.error as string).split('|', 2)
        : ['This signing session has ended', 'Please ask your banker to start a new one.'];
      return (
        <div style={centerWrapS} className="kiosk-center">
          <div style={centerCardS}>
            <h1 style={bigS}>{title}</h1>
            <p style={subS}>{message}</p>
          </div>
        </div>
      );
    }
    // Remote link (BACKLOG-1738): the e-mailed link opens to the e-mail + code
    // challenge — nothing is served until the e-mailed one-time code is verified.
    if (st === 'otp-required') {
      const challenge = (state as unknown as { challenge?: RemoteChallenge | null }).challenge ?? null;
      return <RemoteChallengeGate token={token} challenge={challenge} onChanged={() => { void fetchState(); }} />;
    }
    if (justSaved) {
      return (
        <div style={centerWrapS} className="kiosk-center">
          <div style={centerCardS}>
            <div style={checkS}>✓</div>
            <h1 style={bigS}>
              {justSaved === 'consent' ? 'Consent saved' : justSaved === 'review' ? 'Disclosure acknowledged' : 'Signature saved'}
            </h1>
            <p style={subS}>
              {state?.surface === 'rcp-remote'
                ? 'Thank you. One moment while we bring up your next document…'
                : 'Thank you. One moment while your banker brings up the next item…'}
            </p>
          </div>
        </div>
      );
    }
    if (reviewStep) {
      return (
        <div style={centerWrapS} className="kiosk-center">
          <div style={{ width: '100%', maxWidth: 1100 }}>
            {progressLine(reviewStep.documentId)}
            <KioskReviewStep
              key={`${reviewStep.documentId}-${reviewStep.signerIndex}`}
              token={token}
              documentId={reviewStep.documentId}
              docTitle={reviewStep.docTitle}
              signerName={reviewStep.signerName}
              onSubmittingChange={handleConsentSubmitting}
              onSaved={handleReviewSaved}
            />
          </div>
        </div>
      );
    }
    if (consentStep) {
      return (
        <div style={centerWrapS} className="kiosk-center">
          <div style={{ width: '100%', maxWidth: 820 }}>
            {isRemote && plan.length > 0 && <RemotePlanList plan={plan} />}
            <KioskConsentStep
              key={`${consentStep.documentId}-${consentStep.signerIndex}`}
              token={token}
              signerName={consentStep.signerName}
              onSubmittingChange={handleConsentSubmitting}
              onSaved={handleConsentSaved}
            />
          </div>
        </div>
      );
    }
    if (captureStep) {
      if (isDesktop) {
        // Desktop: left nav rail (brand + signer + doc label + signature
        // controls) so the document viewer gets the full remaining canvas.
        return (
          <div style={desktopRowS}>
            <div style={railS}>
              <div style={railBrandS}>
                <PcbLogo width={72} />
                <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: 0.2 }}>Document Signing</div>
              </div>
              <div style={railBodyS}>
                {captureStep.signerName && (
                  <div style={railSignerChipS}>
                    Now signing
                    <br />
                    <strong style={{ fontSize: 16 }}>{captureStep.signerName}</strong>
                  </div>
                )}
                {progressLine(captureStep.documentId)}
                <h2 style={railDocTitleS}>{captureStep.docTitle ?? 'Please review this document'}</h2>
                {renderRailActions(captureStep)}
              </div>
            </div>
            <div style={desktopDocAreaS}>
              <KioskDocumentViewer
                token={token}
                documentId={captureStep.documentId}
                docTitle={captureStep.docTitle}
                showTitle={false}
              />
            </div>
          </div>
        );
      }
      return (
        <div style={captureRootS}>
          {progressLine(captureStep.documentId)}
          <div style={docAreaS}>
            <KioskDocumentViewer
              token={token}
              documentId={captureStep.documentId}
              docTitle={captureStep.docTitle}
            />
          </div>
          {renderActionBar(captureStep)}
        </div>
      );
    }
    if (isRemote && (st === 'completed' || st === 'captured')) {
      return <RemoteDone token={token} state={state} plan={plan} onReverify={() => { void fetchState(); }} />;
    }
    if (st === 'completed') {
      // Terminal message per Bill 2026-07-17: after the signing session is complete, the
      // kiosk should read "This signing session has ended." (If another signer is up next,
      // the FSR's next push flips the state back to active and the ready screen names them.)
      return (
        <div style={centerWrapS} className="kiosk-center">
          <div style={centerCardS}>
            <div style={checkS}>✓</div>
            <h1 style={bigS}>This signing session has ended</h1>
            <p style={subS}>Thank you — your banker will take it from here.</p>
          </div>
        </div>
      );
    }
    // BACKLOG-1434 — the server says this application has NOTHING signable, or it
    // refused this signer's step. Before this guard the customer sat on a friendly
    // "Ready when you are" screen for a ceremony that could never show them a
    // document — and on 2026-08-03 signers finished such a session twice without
    // ever seeing what they signed. Customer-facing tone: calm, no jargon, and it
    // never invites a signature.
    if (state?.packageSignableCount === 0 || state?.lastError) {
      return (
        <div style={centerWrapS} className="kiosk-center">
          <div style={centerCardS}>
            <h1 style={bigS}>One moment</h1>
            <p style={subS}>
              {isRemote
                ? `Something on our side needs attention before you can sign. Nothing is needed from you right now — please call us at ${BANK_PHONE} and we'll sort it out.`
                : 'Your banker needs to take care of something before you sign. Nothing is needed from you right now — please hand the screen back to them.'}
            </p>
          </div>
        </div>
      );
    }

    // open / active-no-step / captured → waiting for the FSR to push the next doc.
    //
    // Per-signer ready messaging (Bill 2026-07-17): name WHO is up, and in multi-signer
    // sessions say whether they're the first or the next in line — the tablet passes
    // between people, so "Ready when you are" told nobody anything. Server sends
    // currentSignerName (present even between documents) + signerTotal +
    // completedSignerCount; all optional, and with none of them we fall back to the old
    // generic line rather than guessing.
    {
      const readyName = state?.currentSignerName ?? null;
      const total = state?.signerTotal ?? 0;
      const done = state?.completedSignerCount ?? 0;
      let heading = 'Ready when you are';
      if (readyName) {
        heading = total <= 1
          ? `Ready for Signer: ${readyName}`
          : done === 0
            ? `Ready for First Signer: ${readyName}`
            : `Ready for Next Signer: ${readyName}`;
      }
      return (
        <div style={centerWrapS} className="kiosk-center">
          <div style={centerCardS}>
            {/* A remote session moves on by itself — never tell a remote signer to wait for a banker
                (Bill 2026-10-06 phone test). */}
            <h1 style={bigS}>{state?.surface === 'rcp-remote' ? 'Getting your documents ready…' : heading}</h1>
            <p style={subS}>
              {state?.surface === 'rcp-remote'
                ? 'This only takes a moment. If nothing appears, refresh this page.'
                : 'Your banker will bring up each document here for your signature.'}
            </p>
          </div>
        </div>
      );
    }
  }

  // Desktop capture steps drop the top header — the left rail owns the brand.
  const desktopCapture = isDesktop && !!captureStep && !justSaved;

  return (
    <div style={rootS}>
      {!desktopCapture && (
        <div style={headerS}>
          <PcbLogo />
          <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: 0.2 }}>Document Signing</div>
        </div>
      )}
      <div style={bodyS}>{renderInner()}</div>
      <div style={versionFooterS}>PCB Portal &middot; v{PORTAL_VERSION}</div>

      {/* Adopt-your-signature overlay (draw or typed-style) — first sig step / Change. */}
      {adopting && captureStep && (
        <AdoptSignatureModal
          signerName={captureStep.signerName ?? ''}
          defaultTab={captureStep.captureMode === 'typed' ? 'typed' : 'drawn'}
          allowTyped={allowTypedSignature}
          busy={submitting}
          onAdopt={handleAdopt}
          onCancel={() => setAdopting(false)}
        />
      )}

      {/* Click-attestation overlay — reuses the shared ClickToSignCapture primitive. */}
      {clicking && captureStep && (
        <div style={overlayS}>
          <SignatureCaptureRouter
            key={`click-${captureStep.documentId}-${captureStep.signerIndex}`}
            mode="click"
            signerName={captureStep.signerName ?? ''}
            documentTitle={captureStep.docTitle ?? 'this document'}
            isTouchDevice
            disabled={submitting}
            onCapture={(r) => void handleClickCapture(r)}
            onCancel={() => setClicking(false)}
          />
        </div>
      )}
    </div>
  );
}

export default SignerKiosk;
